/**
 * Calendar page (DESIGN.md T13, plan §3.1): command bar (Today, previous/next, range, view switch), the time grid /
 * month grid / agenda with all shown calendars overlaid in their colours, the event panel and the dialogs.
 */
import { useI18n } from '@platform/i18n';
import {
  ActionOverflowMenu,
  Alert,
  BREAKPOINTS,
  Button,
  CalendarMonthGrid,
  CalendarTimeGrid,
  ContextMenuArea,
  resolveActions,
  cn,
  EmptyState,
  ErrorState,
  HoverPreview,
  calendarPrintDocument,
  printHtml,
  MenuSelect,
  sameDay,
  Skeleton,
  useAsync,
  useErrorText,
  useMediaQuery,
  type CalendarGridEvent,
} from '@platform/ui';
import { CalendarSearchIcon, WifiOffIcon, ChevronLeftIcon, ChevronRightIcon, LockIcon, RepeatIcon, SettingsIcon, TriangleAlertIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, type CalendarEvent } from '../api';
import { AddCalendarDialog } from '../calendar/add-calendar-dialog';
import { useCalendar } from '../calendar/calendar-context';
import { EventEditor } from '../calendar/event-editor';
import { EventPanel } from '../calendar/event-panel';
import { toShownEvents, type ShownEvent } from '../calendar/events';
import { CALENDAR_VIEWS, stepDate, useCalendarFormat, viewRange, type CalendarView } from '../calendar/format';
import { CalendarNameDialog } from '../calendar/calendar-name-dialog';
import { SharingDialog } from '../calendar/sharing-dialog';
import { BookingRequestsDialog, InvitationSettingsDialog } from '../calendar/booking-requests';
import { useCalendarInteractions } from '../calendar/interactions';
import { editable } from '../actions/calendar-actions';
import { CalendarSettingsDialog } from '../calendar/settings-dialog';
import { zoomHourHeight } from '../calendar/settings';
import { useTimeGridSettings } from '../calendar/grid-settings';
import { EventPreview } from '../calendar/event-preview';
import { usePendingDeletes } from '../calendar/undo';
import { useOnline } from '../calendar/online';
import { SearchResults } from '../calendar/event-search';
import { ImportDialog, PublishDialog } from '../calendar/feeds-dialogs';
import { calendarName } from '../calendar/events';
import { GoToDateDialog, ShortcutHelpDialog, useCalendarShortcuts } from '../calendar/shortcuts';

/** Horizontal movement (px) of a swipe that changes the period. */
const SWIPE_PX = 60;

const VIEW_LABEL = {
  threeDay: 'webmail.calendarUx.views.threeDay',
  custom: 'webmail.calendarUx.views.custom',
  day: 'webmail.calendar.views.day',
  workWeek: 'webmail.calendar.views.workWeek',
  week: 'webmail.calendar.views.week',
  month: 'webmail.calendar.views.month',
  agenda: 'webmail.calendar.views.agenda',
} as const;

export function CalendarPage() {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const errorText = useErrorText();
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const calendar = useCalendar();
  const { date, setDate, view, setView, calendars, visible, selectedId, setSelectedId, editor, dialog, setDialog, version, changed, settings, updateSettings, categories } = calendar;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const gridSettings = useTimeGridSettings();
  const pendingDeletes = usePendingDeletes();
  const online = useOnline();
  const [params, setParams] = useSearchParams();
  const [instant, setNow] = useState(() => new Date());
  const now = calendar.zone.toWall(instant);

  // Deep link from the mail reading pane: /calendar?date=yyyy-mm-dd.
  const linkedDate = params.get('date');
  useEffect(() => {
    if (!linkedDate) return;
    const parsed = new Date(`${linkedDate}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) setDate(parsed);
    setParams({}, { replace: true });
  }, [linkedDate, setDate, setParams]);

  // The now-line moves every minute.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const range = viewRange(view, date, format.weekStart, settings.customDays);
  const ids = useMemo(() => (calendars.data ?? []).map((c) => c.id).filter((id) => visible.has(id)), [calendars.data, visible]);
  const key = `${range.start.getTime()}|${range.end.getTime()}|${ids.join(',')}|${version}`;
  const loaded = useAsync(() => (ids.length > 0 ? api.listEvents(ids, calendar.zone.fromWall(range.start).toISOString(), calendar.zone.fromWall(range.end).toISOString(), calendar.zone.zone) : Promise.resolve([])), key);

  const title = (event: CalendarEvent) => {
    if (event.masked) return event.sensitivity === 'private' ? t('webmail.calendar.privateEvent') : t(event.showAs === 'tentative' ? 'webmail.calendar.showAs.tentative' : event.showAs === 'oof' ? 'webmail.calendar.showAs.oof' : 'webmail.calendar.showAs.busy');
    return event.subject || t('webmail.calendar.noTitle');
  };
  const shown = useMemo<ShownEvent[]>(() => {
    const list = toShownEvents(loaded.data ?? [], calendars.data ?? [], title, categories, calendar.zone.toWall).filter(
      (item) => calendar.categoryFilter.size === 0 || item.event.categories.some((name) => calendar.categoryFilter.has(name)),
    ).filter((item) => !pendingDeletes.has(item.event.id) && (!settings.hideDeclined || item.event.myResponse !== 'declined'));
    for (const item of list) {
      const badges = [
        item.event.isRecurring && <RepeatIcon key="r" aria-hidden className="size-3 shrink-0" />,
        item.event.sensitivity === 'private' && !item.event.masked && <LockIcon key="p" aria-hidden className="size-3 shrink-0" />,
      ].filter(Boolean);
      if (badges.length > 0) item.grid.badges = badges;
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- title depends on t only
  }, [loaded.data, calendars.data, t, settings.hideDeclined, pendingDeletes, categories, calendar.categoryFilter, calendar.zone]);
  const failed = (loaded.data ?? []).filter((result) => result.error);
  const print = () =>
    printHtml(
      calendarPrintDocument(view === 'month' ? range.days.filter((d) => d.getMonth() === date.getMonth()) : range.days, shown.map((s) => s.grid), {
        title: format.rangeLabel(view, range, date),
        allDay: t('webmail.calendar.allDay'),
        empty: t('webmail.calendarUx.print.empty'),
        dayLabel: format.dayLabel,
        timeRange: (e) => format.range(e.start, e.end, false),
        source: (e) => {
          const item = shown.find((s) => s.grid.id === e.id);
          return item ? calendarName(item.calendar, t) : '';
        },
      }),
    );
  const interactions = useCalendarInteractions(shown, { print });
  const [helpOpen, setHelpOpen] = useState(false);
  const { searchQuery, setSearchQuery } = calendar;
  const swipe = useRef<{ x: number; y: number } | undefined>(undefined);
  const [goToOpen, setGoToOpen] = useState(false);
  const selectedItem = shown.find((s) => s.grid.id === selectedId);
  // Letters type into Quick add while free time is selected (Outlook); the shortcuts wait.
  const letter = (run: () => void) => () => {
    if (interactions.selection) return false;
    run();
    return true;
  };
  const onEvent = (run: (item: ShownEvent) => void) => () => {
    if (!selectedItem || (window.getSelection()?.toString() ?? '') !== '') return false;
    run(selectedItem);
    return true;
  };
  useCalendarShortcuts({
    'calendar.new': letter(() => interactions.openSelection()),
    'calendar.quickAdd': letter(() => interactions.quickAdd()),
    'calendar.today': letter(() => setDate(calendar.zone.toWall(new Date()))),
    'calendar.next': letter(() => setDate(stepDate(view, date, 1, settings.customDays))),
    'calendar.previous': letter(() => setDate(stepDate(view, date, -1, settings.customDays))),
    'calendar.viewDay': letter(() => setView('day')),
    'calendar.viewWorkWeek': letter(() => setView('workWeek')),
    'calendar.viewWeek': letter(() => setView('week')),
    'calendar.viewMonth': letter(() => setView('month')),
    'calendar.viewAgenda': letter(() => setView('agenda')),
    'calendar.goTo': letter(() => setGoToOpen(true)),
    'calendar.help': () => setHelpOpen(true),
    'calendar.print': () => {
      print();
      return true;
    },
    'calendar.search': letter(() => document.getElementById('calendar-shell-search')?.focus()),
    'calendar.open': onEvent((item) => (editable(item) ? interactions.eventHandlers.edit(item) : setSelectedId(item.grid.id))),
    'calendar.delete': onEvent((item) => item.event.canDelete && !item.event.masked && interactions.eventHandlers.remove(item)),
    'calendar.copy': onEvent((item) => interactions.eventHandlers.copy(item)),
    'calendar.duplicate': onEvent((item) => interactions.eventHandlers.duplicate(item)),
    'calendar.paste': () => {
      if (!interactions.canPaste) return false;
      interactions.pasteAt(interactions.menuSlot);
      return true;
    },
  });
  const grid = interactions.applyOverrides(shown.map((s) => s.grid));
  const selected = shown.find((s) => s.grid.id === selectedId);

  const eventLabel = (event: CalendarGridEvent) => {
    const item = shown.find((s) => s.grid.id === event.id);
    const other = item && item.calendar.kind !== 'own' ? item.calendar.name : '';
    return t('webmail.calendar.eventLabel', {
      title: event.title,
      time: format.range(event.start, event.end, event.allDay),
      hasLocation: event.detail ? 'yes' : 'no',
      location: event.detail ?? '',
      hasCalendar: other ? 'yes' : 'no',
      calendar: other,
    });
  };
  const goTo = (day: Date, nextView?: CalendarView) => {
    setDate(day);
    if (nextView) setView(nextView);
  };

  const viewSwitch = isDesktop ? (
    <div role="radiogroup" aria-label={t('webmail.calendar.views.label')} className="flex rounded-md border p-0.5">
      {CALENDAR_VIEWS.filter((option) => option !== 'threeDay').map((option) => (
        <Button
          key={option}
          role="radio"
          aria-checked={view === option}
          variant="ghost"
          size="sm"
          className={cn(view === option && 'bg-accent font-semibold')}
          onClick={() => setView(option)}
        >
          {t(VIEW_LABEL[option], { n: settings.customDays })}
        </Button>
      ))}
    </div>
  ) : (
    <MenuSelect label={t('webmail.calendar.views.label')} value={view} options={CALENDAR_VIEWS.map((option) => ({ value: option, label: t(VIEW_LABEL[option], { n: settings.customDays }) }))} onChange={setView} />
  );

  let body;
  if (searchQuery) body = <SearchResults query={searchQuery} calendarIds={ids} onClose={() => setSearchQuery(undefined)} />;
  else if (calendars.error) body = <ErrorState description={errorText.describe(calendars.error)} onRetry={calendars.reload} />;
  else if (loaded.error) body = <ErrorState description={errorText.describe(loaded.error)} onRetry={loaded.reload} />;
  else if (!loaded.data && loaded.loading) {
    body = (
      <div className="flex flex-col gap-3 p-4" aria-busy="true">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  } else if (view === 'month') {
    body = (
      <CalendarMonthGrid
        month={date}
        weekStartsOn={format.weekStart}
        events={grid}
        now={now}
        selectedId={selectedId}
        onSelectEvent={setSelectedId}
        onSelectDay={(day) => goTo(day, 'day')}
        formatWeekday={format.weekday}
        formatDayNumber={format.dayNumber}
        formatDayLabel={format.dayLabel}
        eventLabel={eventLabel}
        moreLabel={(n) => t('webmail.calendar.more', { n })}
        label={t('webmail.calendar.views.month')}
        canDrag={(event) => { const item = shown.find((s) => s.grid.id === event.id); return Boolean(item && editable(item)); }}
        onChangeEvent={interactions.changeTimes}
        onCopyEvent={interactions.copyTo}
        onSelectRange={interactions.selectRange}
        selection={interactions.selection}
        onOpenSelection={interactions.openSelection}
        formatTime={format.time}
        formatSelection={gridSettings.formatSelection}
        weekNumberLabel={settings.weekNumbers ? format.rowWeekNumber : undefined}
      />
    );
  } else if (view === 'agenda') {
    body = <Agenda days={range.days} items={shown} selectedId={selectedId} onSelect={setSelectedId} now={now} />;
  } else {
    body = (
      <CalendarTimeGrid
        days={range.days}
        events={grid}
        now={now}
        selectedId={selectedId}
        onSelectEvent={setSelectedId}
        onSelectRange={interactions.selectRange}
        selection={interactions.selection}
        onOpenSelection={interactions.openSelection}
        canDrag={interactions.canDrag}
        onChangeEvent={interactions.changeTimes}
        onCopyEvent={interactions.copyTo}
        onSelectDay={view === 'day' ? undefined : (day) => goTo(day, 'day')}
        formatWeekday={format.weekday}
        formatDayNumber={format.dayNumber}
        formatHour={format.hour}
        eventLabel={eventLabel}
        formatTimeRange={(event) => format.range(event.start, event.end, false)}
        labels={{ allDay: t('webmail.calendar.allDay'), grid: t(VIEW_LABEL[view], { n: settings.customDays }) }}
        {...gridSettings}
        weekLabel={settings.weekNumbers && range.days[0] ? (range.days.length === 7 ? format.rowWeekNumber(range.days[0]) : format.weekNumber(range.days[0])) : undefined}
        onZoom={(direction) => updateSettings({ hourHeight: zoomHourHeight(settings.hourHeight, direction), fitWorkingHours: false })}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <div role="toolbar" aria-label={t('webmail.calendar.toolbar')} className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
          <Button variant="outline" size="sm" onClick={() => setDate(calendar.zone.toWall(new Date()))}>
            {t('webmail.calendar.today')}
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={t('webmail.calendar.previous')} onClick={() => setDate(stepDate(view, date, -1, settings.customDays))}>
            <ChevronLeftIcon />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={t('webmail.calendar.next')} onClick={() => setDate(stepDate(view, date, 1, settings.customDays))}>
            <ChevronRightIcon />
          </Button>
          <h1 className={cn('min-w-0 font-semibold', isDesktop ? 'flex-1 truncate text-base' : 'order-last basis-full text-sm')} aria-live="polite">
            {format.rangeLabel(view, range, date, !isDesktop)}
          </h1>
          {viewSwitch}
          <Button variant="ghost" size="icon-sm" aria-label={t('webmail.calendarUx.shortcuts.goTo')} title={t('webmail.calendarUx.shortcuts.goTo')} onClick={() => setGoToOpen(true)}>
            <CalendarSearchIcon />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={t('webmail.calendarUx.settings.open')} title={t('webmail.calendarUx.settings.open')} onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </Button>
          <ActionOverflowMenu actions={resolveActions(interactions.slotActions, [interactions.menuSlot], 'overflow')} label={t('webmail.calendar.actions.newMenu')} />
        </div>
        {failed.length > 0 && (
          <Alert variant="warning" className="m-2 w-auto items-center">
            <TriangleAlertIcon />
            <span className="flex-1">{t('webmail.calendar.someUnavailable', { names: format.list(failed.map((f) => { const c = calendars.data?.find((x) => x.id === f.calendarId); return c ? calendarName(c, t) : f.calendarId; })) })}</span>
            <Button size="sm" variant="outline" onClick={loaded.reload}>
              {t('webmail.calendarUx.offline.retry')}
            </Button>
          </Alert>
        )}
        {!online && (
          <Alert variant="warning" className="m-2 w-auto items-center" role="status">
            <WifiOffIcon />
            <span>{t('webmail.calendarUx.offline.banner')}</span>
          </Alert>
        )}
        <ContextMenuArea menuFor={interactions.menuFor}>
          {/* Enter on a selection opens the event window, typing starts Quick add (interactions.tsx); double-click opens an event. */}
          <div
            className="flex min-h-0 flex-1 flex-col"
            onDoubleClick={interactions.onGridDoubleClick}
            // Phones: a horizontal swipe moves one period (a long press still drags events).
            onTouchStart={(e) => {
              const touch = e.touches[0];
              swipe.current = touch && e.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : undefined;
            }}
            onTouchEnd={(e) => {
              const from = swipe.current;
              const touch = e.changedTouches[0];
              swipe.current = undefined;
              if (!from || !touch) return;
              const dx = touch.clientX - from.x;
              const dy = touch.clientY - from.y;
              if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > 2 * Math.abs(dy)) setDate(stepDate(view, date, dx < 0 ? 1 : -1, settings.customDays));
            }}
          >
            <HoverPreview
              disabled={!isDesktop}
              render={(id) => {
                const item = shown.find((s) => s.grid.id === id);
                return item ? <EventPreview item={item} onOpen={() => setSelectedId(item.grid.id)} /> : undefined;
              }}
            >
              {body}
            </HoverPreview>
          </div>
        </ContextMenuArea>
      </div>
      {selected && (
        <EventPanel
          key={selected.grid.id}
          menu={<ActionOverflowMenu actions={resolveActions(interactions.eventActions, [selected], 'overflow')} title={selected.grid.title} />}
          item={selected}
          onClose={() => setSelectedId(undefined)}
          onChanged={() => {
            setSelectedId(undefined);
            changed();
          }}
        />
      )}
      {editor && <EventEditor key={editor.mode === 'edit' ? editor.event.id : 'new'} />}
      {dialog?.kind === 'import' && <ImportDialog calendar={dialog.calendar} onClose={() => setDialog(undefined)} />}
      {dialog?.kind === 'publish' && <PublishDialog calendar={dialog.calendar} onClose={() => setDialog(undefined)} />}
      <SharingDialog calendar={dialog?.kind === 'sharing' ? dialog.calendar : undefined} onClose={() => setDialog(undefined)} />
      <AddCalendarDialog open={dialog?.kind === 'add'} onOpenChange={(open) => !open && setDialog(undefined)} />
      {(dialog?.kind === 'create' || dialog?.kind === 'rename') && <CalendarNameDialog calendar={dialog.kind === 'rename' ? dialog.calendar : undefined} onClose={() => setDialog(undefined)} />}
      <BookingRequestsDialog open={dialog?.kind === 'bookingRequests'} onClose={() => setDialog(undefined)} onChanged={changed} />
      <InvitationSettingsDialog open={dialog?.kind === 'invitations'} onClose={() => setDialog(undefined)} />
      {interactions.dialogs}
      <CalendarSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ShortcutHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      {goToOpen && <GoToDateDialog open initial={date} onOpenChange={setGoToOpen} onGo={setDate} />}
    </div>
  );
}

/** Agenda view: the next days as a list, grouped by day. */
function Agenda({ days, items, selectedId, onSelect, now }: { days: readonly Date[]; items: readonly ShownEvent[]; selectedId: string | undefined; onSelect: (id: string) => void; now: Date }) {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const groups = days
    .map((day) => ({ day, list: items.filter((s) => s.grid.start < new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1) && (s.grid.end > day || s.grid.start >= day)).sort((a, b) => Number(b.grid.allDay) - Number(a.grid.allDay) || a.grid.start.getTime() - b.grid.start.getTime()) }))
    .filter((g) => g.list.length > 0);
  if (groups.length === 0) return <EmptyState title={t('webmail.calendar.agendaEmpty')} description={t('webmail.calendar.agendaEmptyDescription')} />;
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-3">
      {groups.map(({ day, list }) => (
        <section key={day.getTime()} aria-label={format.dayLabel(day)} className="mb-4">
          <h2 className={cn('mb-1 text-sm font-semibold', sameDay(day, now) && 'text-primary')}>{format.dayLabel(day)}</h2>
          <ul className="flex flex-col divide-y rounded-md border">
            {list.map((s) => (
              <li key={s.grid.id}>
                <button
                  type="button"
                  onClick={() => onSelect(s.grid.id)}
                  aria-pressed={selectedId === s.grid.id}
                  className={cn('flex w-full items-center gap-3 px-3 py-2 text-left text-sm outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring', selectedId === s.grid.id && 'bg-accent')}
                >
                  <span className="w-28 shrink-0 text-xs text-muted-foreground">{s.grid.allDay ? t('webmail.calendar.allDay') : format.range(s.grid.start, s.grid.end, false)}</span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-medium">{s.grid.title}</span>
                    {s.grid.detail && <span className="truncate text-xs text-muted-foreground">{s.grid.detail}</span>}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">{s.calendar.kind === 'own' ? '' : s.calendar.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
